import { GetTextModel } from '../../../providers'
import { GroupedSelectOption, SelectOption, SelectOptionValue } from '../../../types'
import { getTypeResponseTranslation } from '../../../../../MyStichtingOpenHelpers/src/static'

const MESSAGE_KEYS = {
    RESULTS: 'STO.COMMON.RESULTS',
}

export interface SelectListItem {
    value?: SelectOptionValue
    name?: string | null
}

export interface DefaultTypeResponse {
    value?: number
    name?: string | null
    translation?: string | null
    color?: number | null
}

interface MapAsyncSelectInputResponseToOptionsProps<T> {
    selectItems: T[]
    totalResults: number | string
    getText: GetTextModel
    formatName?: (selectItem: SelectListItem) => string
    formatFooter?: (selectItem: SelectListItem) => string
}

export const mapAsyncSelectInputResponseToOptions = <T extends SelectListItem>({
    selectItems,
    totalResults,
    getText,
    formatName,
    formatFooter,
}: MapAsyncSelectInputResponseToOptionsProps<T>): GroupedSelectOption<T>[] => {
    const mappedSelectOptions: SelectOption<T>[] = selectItems.map(
        (selectItem): SelectOption<T> => ({
            label: formatName ? formatName(selectItem) : (String(selectItem.name) ?? String(selectItem.value)),
            value: selectItem.value as SelectOptionValue,
            ...(formatFooter ? { footer: formatFooter(selectItem) } : {}),
            data: selectItem,
        }),
    )

    return [
        {
            label: `${getText(MESSAGE_KEYS.RESULTS)} (${selectItems.length}/${totalResults})`,
            options: mappedSelectOptions,
        },
    ]
}

export interface MapSelectInputResponseToOptionsProps<T> {
    selectItems: T[]
    formatName?: (selectItem: unknown) => string
    formatFooter?: (selectItem: unknown) => string
    /** In some cases, like "named enums" from the back-end, they expect the "name" of the enum key instead of the value.
     * This boolean allows us to use the name instead of the value */
    useNameAsValue?: boolean
}

export const mapSelectInputResponseToOptions = <T extends SelectListItem>({
    selectItems,
    formatName,
    formatFooter,
    useNameAsValue,
}: MapSelectInputResponseToOptionsProps<T>): SelectOption<T>[] => {
    const mappedSelectOptions: SelectOption<T>[] = selectItems.map((selectItem) => ({
        label: formatName ? formatName(selectItem) : (String(selectItem.name) ?? String(selectItem.value)),
        value: useNameAsValue && selectItem.name ? selectItem.name : (selectItem.value ?? null),
        ...(formatFooter ? { footer: formatFooter(selectItem) } : {}),
        data: selectItem,
    }))

    return mappedSelectOptions
}

export const mapTypeResponseListToOptions = <T extends DefaultTypeResponse = DefaultTypeResponse>(
    data: T[],
    valueKey: string = 'value',
    formatLabel?: (option: T) => string,
): SelectOption<T>[] => {
    return data.map((typeResponse) => ({
        value: typeResponse?.[valueKey as keyof T] as string | number,
        label: formatLabel?.(typeResponse) ?? getTypeResponseTranslation(typeResponse) ?? '',
    }))
}
